import { useEffect, useMemo, useState } from "react";
import {
  List,
  Stack,
  Button,
  Checkbox,
  Collapse,
  ListItem,
  ListItemText,
  ListItemButton,
  FormControlLabel
} from "@material-ui/core";
import { NewMultipleContactPicker } from "./NewContactPicker";
import { Close, Message } from "@material-ui/icons";
import { useModal } from "hooks/useModal";
import { getFullName } from "utils/Haim/utils";
import { NewBulkMessageDialog } from "../contacts/modal/bulkMessage/NewBulkMessageDialog";
import useContactsLookup from "hooks/contacts/useContactsLookup";
import { useTranslation } from "react-i18next";
import type { AlgoliaHit } from "instantsearch.js";
import type { ProcessedListingData, RealEstateContact } from "../../../@types/sharedSchema";
import ForwardSendIcon from "../../ForwardSendIcon";

type BuyerMatchedBulkMessageProps = {
  loading: boolean;
  listing: AlgoliaHit<ProcessedListingData>;
  selectedContacts: RealEstateContact[];
};

type AdditionalContactCheckboxes = Record<string, boolean>;

export function BuyerMatchedBulkMessage({
  listing,
  loading,
  selectedContacts
}: BuyerMatchedBulkMessageProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const [additionalSelectedContacts, setAdditionalSelectedContacts] = useState<RealEstateContact[]>(
    []
  );

  const [additionalContactCheckboxes, setAdditionalContactCheckboxes] =
    useState<AdditionalContactCheckboxes>({});

  const {
    open: bulkMessageDialogOpen,
    openModal: openBulkMessageDialog,
    closeModal: closeBulkMessageDialog
  } = useModal();


  const { t: translate } = useTranslation();

  useEffect(() => {
    const newAdditionalSelectedContacts = additionalSelectedContacts.filter(
      (contact) => !selectedContacts.some((c) => c.id === contact.id)
    );

    const skipFilteringAdditionalSelectedContacts =
      newAdditionalSelectedContacts.length === additionalSelectedContacts.length;

    if (skipFilteringAdditionalSelectedContacts) return;

    setAdditionalSelectedContacts(newAdditionalSelectedContacts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContacts]);

  const parsedSelectedContacts = [...selectedContacts];

  const additionalContactsNotInMainList = additionalSelectedContacts.filter(
    (contact) => !selectedContacts.some((c) => c.id === contact.id)
  );

  if (additionalContactsNotInMainList.length) {
    parsedSelectedContacts.push(...additionalContactsNotInMainList);
  }

  useEffect(() => {
    const newCheckboxes: AdditionalContactCheckboxes = {};

    for (const contact of parsedSelectedContacts) {
      newCheckboxes[contact.id] = additionalContactCheckboxes[contact.id] ?? true;
    }

    setAdditionalContactCheckboxes(newCheckboxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContacts]);

  const handleChangeAdditionalSelectedContacts = (newContacts: RealEstateContact[]): void => {
    for (const contact of newContacts) {
      const isAlreadyInList = parsedSelectedContacts.some((c) => c.id === contact.id);

      if (isAlreadyInList) continue;

      setAdditionalContactCheckboxes((prev) => ({
        ...prev,
        [contact.id]: true
      }));
    }

    setAdditionalSelectedContacts(newContacts);
  };

  const handleToggleCheckbox = (contactId: string) => (): void => {
    setAdditionalContactCheckboxes((prev) => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const toggleOpen = (): void => setOpen(!open);

  // The names in the list below: one lookup for everyone on it. Only the names — who is on
  // the list, and who is ticked, is decided by the ids alone.
  const contactIds = useMemo(
    () => [...selectedContacts, ...additionalSelectedContacts].map(({ id }) => id),
    [selectedContacts, additionalSelectedContacts]
  );
  const { lookup } = useContactsLookup(contactIds);

  const finalContactsToMessage = parsedSelectedContacts.filter(
    (contact) => additionalContactCheckboxes[contact.id]
  );

  const totalContactsInList = parsedSelectedContacts.length;
  const totalContactsSelected = finalContactsToMessage.length;

  const areAllContactsSelected =
    totalContactsInList > 0 && totalContactsSelected === totalContactsInList;
  const areSomeContactsSelected =
    totalContactsSelected > 0 && totalContactsSelected < totalContactsInList;

  const handleSelectAllToggle = (): void => {
    const newCheckedState = !areAllContactsSelected;

    const newCheckboxes: AdditionalContactCheckboxes = {};

    for (const contact of parsedSelectedContacts) {
      newCheckboxes[contact.id] = newCheckedState;
    }

    setAdditionalContactCheckboxes(newCheckboxes);
  };

  const finalContactsToMessageIds = finalContactsToMessage.map(({ id }) => id);

  const bulkMessageButtonDisabled = loading;
  const sendMessageButtonDisabled = loading || !finalContactsToMessage.length;

  const disabledSelectedContacts = selectedContacts.map(({ id }) => id);


  return (
    <Stack gap={2}>
      <NewBulkMessageDialog
        open={bulkMessageDialogOpen}
        listing={listing}
        selected={finalContactsToMessageIds}
        onClose={closeBulkMessageDialog}
      />
      <Stack alignItems="center" direction="row" gap={1}>
        {open ? (
          <>
            <Button
              size="small"
              color="success"
              variant="outlined"
              disabled={sendMessageButtonDisabled}
              endIcon={<ForwardSendIcon />}
              onClick={openBulkMessageDialog}
            >
              {translate("listingMatchToContactsPage.bulkMessage.sendMessageToMatches")}
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={loading}
              startIcon={<Close />}
              onClick={toggleOpen}
            >
              {translate("listingMatchToContactsPage.bulkMessage.exitBulkMessaging")}
            </Button>
          </>
        ) : (
          <Button
            size="small"
            color="info"
            variant="outlined"
            disabled={bulkMessageButtonDisabled}
            startIcon={<Message />}
            onClick={toggleOpen}
          >
            {translate("listingMatchToContactsPage.bulkMessage.bulkMessaging")}
          </Button>
        )}
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack gap={1}>
          <NewMultipleContactPicker
            label={translate("listingMatchToContactsPage.bulkMessage.additionalClient")}
            loading={loading}
            disabledOptions={disabledSelectedContacts}
            selectedContacts={additionalSelectedContacts}
            setSelectedContacts={handleChangeAdditionalSelectedContacts}
          />
          {!!parsedSelectedContacts.length && (
            <>
              <FormControlLabel
                sx={{ ml: 0.75, mb: -1 }}
                label={translate("listingMatchToContactsPage.bulkMessage.selectAll")}
                control={
                  <Checkbox
                    checked={areAllContactsSelected}
                    disabled={loading}
                    indeterminate={areSomeContactsSelected}
                    onChange={handleSelectAllToggle}
                  />
                }
              />
              <List>
                {parsedSelectedContacts.map((contact) => {
                  // The looked-up name; until it is answered (and for a number that is not
                  // in the book) whatever name the selected contact itself carries, which
                  // is none for an `{ id }` stand-in — that row shows the phone.
                  const named = lookup(contact.id) ?? contact;
                  const fullName = getFullName(named.callerFirstName, named.callerLastName);

                  const primary = fullName || contact.id;
                  const secondary = fullName ? contact.id : null;

                  const checked = additionalContactCheckboxes[contact.id] ?? true;

                  return (
                    <ListItem key={contact.id} disablePadding>
                      <ListItemButton
                        sx={{ borderRadius: 1 }}
                        dense
                        disabled={loading}
                        onClick={handleToggleCheckbox(contact.id)}
                      >
                        <Checkbox
                          disableRipple
                          edge="start"
                          checked={checked}
                          disabled={loading}
                          inputProps={{ "aria-labelledby": contact.id }}
                        />
                        <ListItemText id={contact.id} primary={primary} secondary={secondary} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
}
